'use client';

/**
 * ObeyaMatrixCard — full-bleed RFQ vendor comparison matrix.
 *
 * Wraps the existing `<RFQComparisonWidget />` so it paints from the
 * cards-everywhere LayoutEngine on `mode1`. Reads the active RFQ from
 * `useViewportSelection` (entityType === 'rfq' → entityId), with a
 * fallback to the graph store's lastly-selected RFQ so the matrix
 * persists when the user clicks a non-RFQ node — mirrors the legacy
 * obeya widget's `contextRfqSid` behaviour.
 *
 * RFQ-scoped, not project-scoped — the matrix is the per-RFQ vendor
 * comparison. Without a selected RFQ the card paints a "select an
 * RFQ" empty state (the user reaches matrix view by selecting an RFQ
 * first or clicking the chip on a project preset; the empty state
 * tells them what's needed).
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_matrix"
 * @see klyde_ipe/components/widgets/rfq-comparison-widget.tsx
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { LayoutGrid } from 'lucide-react';
import { RFQComparisonWidget } from './rfq-comparison-widget';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

export function ObeyaMatrixCard() {
  const { entityType, entityId } = useViewportSelection();
  // When the user has clicked away from an RFQ (e.g. to a project
  // node in the graph), the RFQ context persists via graph-selection
  // store's `selectedNode` — same persistence the legacy widget
  // implements via its local `contextRfqSid` state.
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  const rfqSid =
    entityType === 'rfq'
      ? entityId ?? null
      : selectedNode?.type === 'rfq'
        ? selectedNode.id ?? null
        : null;
  const rfqLabel =
    entityType === 'rfq' ? null : (selectedNode?.label ?? null);

  if (!rfqSid) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground">
        <div className="p-4 rounded-2xl bg-muted/30 mb-4">
          <LayoutGrid className="w-10 h-10 opacity-40" />
        </div>
        <p className="text-sm font-medium">Select an RFQ</p>
        <p className="text-xs mt-1">
          The matrix compares vendor quotes for one RFQ.
        </p>
      </div>
    );
  }

  // One BFF strategy call — `rfi_comparison`. `<RFQComparisonWidget />`
  // owns the fetch via `useRFQComparison`, which now resolves cleanly
  // through `useCapabilityStore.strategyName('rfi', 'comparison')`
  // after Step 8 of the migration filled in `action="comparison"` on
  // the backend decoration. No card-level fetch wrapper needed.
  return (
    <div className="h-full w-full overflow-hidden">
      <RFQComparisonWidget entityId={rfqSid} title={rfqLabel ?? undefined} />
    </div>
  );
}
